import type { AdminMessageInfo } from '@kyumo/common';
import { ArrowLeft, FolderOpen, Loader2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { formatCost, formatNumber } from '../../components/admin/chart-utils';
import { GroupedAssistantCard, MessageCard } from '../../components/admin/MessageCard';
import { extractPrompts, SystemPromptSection } from '../../components/admin/SystemPromptSection';
import { VersionSelector } from '../../components/admin/VersionSelector';
import { useTaskDetail, useTaskVersion, useTaskVersions } from '../../hooks/use-admin-api';

/**
 * タスク詳細ページ
 */
export function TaskDetailPage() {
  const { email, taskId } = useParams<{ email: string; taskId: string }>();
  const decodedEmail = email ? decodeURIComponent(email) : '';
  const [selectedVersion, setSelectedVersion] = useState<number | undefined>(undefined);

  // タスク詳細を取得（最新バージョン）
  const { data, isLoading, error } = useTaskDetail(decodedEmail, taskId || '');

  // バージョン一覧を取得
  const { data: versionsData, isLoading: isLoadingVersions } = useTaskVersions(
    decodedEmail,
    taskId || '',
  );

  // 特定バージョンを取得（selectedVersion が設定されている場合のみ）
  const { data: versionData, isLoading: isLoadingVersion } = useTaskVersion(
    decodedEmail,
    taskId || '',
    selectedVersion,
  );

  // 初期バージョンを設定（一覧取得後、最新バージョンを選択）
  // biome-ignore lint/correctness/useExhaustiveDependencies: selectedVersion は初期値 undefined 時のみ参照するため依存不要
  useEffect(() => {
    if (
      versionsData?.versions &&
      versionsData.versions.length > 0 &&
      selectedVersion === undefined
    ) {
      const latestVersion = Math.max(...versionsData.versions.map((v) => v.version));
      setSelectedVersion(latestVersion);
    }
  }, [versionsData]);

  // 表示するタスクデータ（バージョン選択時はそのバージョン、それ以外はデフォルト）
  const displayTask = versionData?.task ?? data?.task;

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    );
  }

  if (error || !displayTask) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400">タスクが見つかりません</p>
          <p className="text-muted-foreground text-sm mt-2">
            {error instanceof Error
              ? error.message
              : 'タスクが存在しないか、アクセス権限がありません'}
          </p>
          <Link
            to="/admin"
            className="inline-block mt-4 px-4 py-2 bg-muted text-foreground rounded-lg hover:bg-accent transition-colors"
          >
            ダッシュボードに戻る
          </Link>
        </div>
      </div>
    );
  }

  const task = displayTask;

  return (
    <div className="p-8">
      {/* 戻るリンク */}
      <Link
        to={`/admin/users/${encodeURIComponent(task.userEmail)}`}
        className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-6"
      >
        <ArrowLeft className="w-4 h-4" />
        ユーザー詳細に戻る
      </Link>

      {/* ヘッダー */}
      <div className="mb-8">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-foreground">{task.title || '無題のタスク'}</h1>
              {/* バージョンセレクター */}
              {versionsData?.versions && selectedVersion !== undefined && (
                <VersionSelector
                  versions={versionsData.versions}
                  currentVersion={selectedVersion}
                  onVersionChange={setSelectedVersion}
                  isLoading={isLoadingVersions || isLoadingVersion}
                />
              )}
            </div>
            <div className="flex items-center gap-3 mt-1 text-muted-foreground">
              <Link
                to={`/admin/users/${encodeURIComponent(task.userEmail)}`}
                className="hover:text-primary-400 transition-colors"
              >
                {task.userEmail}
              </Link>
              <span>•</span>
              <span>{new Date(task.createdAt).toLocaleString('ja-JP')}</span>
              {task.workspaceName && (
                <>
                  <span>•</span>
                  <span className="flex items-center gap-1">
                    <FolderOpen className="w-4 h-4" />
                    {task.workspaceName}
                  </span>
                </>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* タスク合計 */}
            {task.totalTokens !== undefined && task.totalTokens > 0 && (
              <div className="text-right text-sm">
                <div className="text-muted-foreground">
                  合計: {formatNumber(task.totalTokens)} トークン
                </div>
                {task.cost !== undefined && task.cost > 0 && (
                  <div className="text-yellow-400 font-medium">{formatCost(task.cost)}</div>
                )}
              </div>
            )}
            {task.deleted && (
              <span className="px-3 py-1 text-sm bg-red-500/20 text-red-400 rounded-lg">
                削除済み
              </span>
            )}
          </div>
        </div>
      </div>

      {/* メッセージ一覧 */}
      <div className="space-y-4">
        {task.messages && task.messages.length > 0 ? (
          <>
            {/* システムプロンプトセクション（折りたたみ式、オーケストレーターのみ） */}
            <SystemPromptSection
              messages={
                task.messages as Array<{
                  id: string;
                  role: 'user' | 'assistant' | 'system';
                  content?: string;
                  parts?: Array<{ type: string; text?: string }>;
                  createdAt: number;
                }>
              }
            />

            <MessageList messages={task.messages} />
          </>
        ) : (
          <div className="bg-card rounded-xl p-8 border border-border text-center">
            <p className="text-muted-foreground">メッセージがありません</p>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * メッセージ一覧コンポーネント
 *
 * システムメッセージからサブエージェントプロンプトを抽出し、
 * 連続する assistant メッセージをグループ化して表示する。
 */
function MessageList({ messages }: { messages: AdminMessageInfo[] }) {
  const { subagentPrompts, groupedMessages } = useMemo(() => {
    // サブエージェントのシステムプロンプトを抽出
    const systemMessages = messages.filter((m: { role: string }) => m.role === 'system') as Array<{
      id: string;
      role: 'user' | 'assistant' | 'system';
      content?: string;
      parts?: Array<{ type: string; text?: string }>;
      createdAt: number;
    }>;
    const allPrompts = extractPrompts(systemMessages);
    const prompts = allPrompts.filter((p) => p.agentType === 'subagent');

    const nonSystemMessages = messages.filter((message) => message.role !== 'system');

    // メッセージをグループ化（連続するassistantメッセージを統合）
    const groups: Array<{
      id: string;
      role: string;
      messages: AdminMessageInfo[];
    }> = [];

    for (const message of nonSystemMessages) {
      const lastGroup = groups[groups.length - 1];

      if (message.role === 'assistant' && lastGroup?.role === 'assistant') {
        lastGroup.messages.push(message);
      } else {
        groups.push({
          id: message.id,
          role: message.role,
          messages: [message],
        });
      }
    }

    return { subagentPrompts: prompts, groupedMessages: groups };
  }, [messages]);

  return (
    <>
      {groupedMessages.map((group) => {
        if (group.role === 'assistant' && group.messages.length > 1) {
          return (
            <GroupedAssistantCard
              key={group.id}
              messages={group.messages}
              subagentPrompts={subagentPrompts}
            />
          );
        }
        const firstMessage = group.messages[0];
        if (!firstMessage) return null;
        return (
          <MessageCard
            key={firstMessage.id}
            message={firstMessage}
            subagentPrompts={subagentPrompts}
          />
        );
      })}
    </>
  );
}
